import { useState } from 'react';
import { Download, MessageSquare, Phone, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { useInvalidate, useLeads } from '@/lib/queries';
import type { LeadItem, LeadStatus } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { Segmented } from '@/components/MediaPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { Drawer, Select } from '@/components/ui/primitives';
import { Chip, type ChipTone } from '@/components/ui/Chip';
import { EmptyState, TableSkeleton } from '@/components/ui/Feedback';

/**
 * Leads (decision A3) — the one workstream here that is not content or SEO.
 * Kept deliberately plain: a list, a detail drawer, a status, an export. It is
 * not a CRM, and building one here would be the scope creep §15 warns about.
 *
 * These are personal data: the export is audited, and delete is a real delete.
 */
export function Leads() {
  const { t, formatRelative, formatDate } = useI18n();
  const invalidate = useInvalidate();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | LeadStatus>('all');
  const [selected, setSelected] = useState<LeadItem | null>(null);

  const { data, isLoading } = useLeads({ q: query || undefined, status: status === 'all' ? undefined : status });
  const rows = data?.items ?? [];

  const statusLabels: Record<LeadStatus, string> = {
    NEW: t.leads.statusNew,
    CONTACTED: t.leads.statusContacted,
    QUALIFIED: t.leads.statusQualified,
    CLOSED: t.leads.statusClosed,
    SPAM: t.leads.statusSpam,
  };

  // Tone carries the state of the conversation: new needs attention, qualified
  // is a win, closed and spam are settled and should recede.
  const statusTones: Record<LeadStatus, ChipTone> = {
    NEW: 'brand',
    CONTACTED: 'neutral',
    QUALIFIED: 'grass',
    CLOSED: 'quiet',
    SPAM: 'quiet',
  };

  const open = async (lead: LeadItem) => {
    setSelected(lead);
    if (!lead.isRead) {
      await api.patch(`/leads/${lead.id}`, { isRead: true }).catch(() => undefined);
      void invalidate(['leads']);
    }
  };

  return (
    <>
      <PageHeader
        title={t.leads.title}
        subtitle={t.leads.subtitle}
        action={
          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={() => window.open(`${import.meta.env.VITE_API_URL}/leads/export.csv`, '_blank')}
          >
            {t.leads.export}
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[14rem] max-w-sm flex-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.common.search}
              aria-label={t.common.search}
              className="pl-9"
            />
          </div>
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: t.common.all, badge: data?.unread },
              { value: 'NEW', label: t.leads.statusNew },
              { value: 'CONTACTED', label: t.leads.statusContacted },
              { value: 'QUALIFIED', label: t.leads.statusQualified },
            ]}
          />
        </div>
      </PageHeader>

      <div className="mx-auto w-full max-w-content px-6 pb-12">
        <div className="card overflow-hidden">
          {isLoading ? (
            <TableSkeleton rows={5} cols={4} />
          ) : rows.length === 0 ? (
            <EmptyState icon={<MessageSquare className="h-6 w-6" />} title={t.leads.emptyTitle} hint={t.leads.emptyHint} />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((lead) => (
                <li key={lead.id}>
                  <button
                    type="button"
                    onClick={() => void open(lead)}
                    className="row flex w-full cursor-pointer items-center gap-4 px-4 py-3 text-left"
                  >
                    {/* Unread is a filled dot in brand, not bold text: it has to
                        be scannable down a column of forty rows. */}
                    <span
                      aria-hidden
                      className={cn(
                        'h-1.5 w-1.5 shrink-0 rounded-chip',
                        lead.isRead ? 'bg-transparent' : 'bg-brand-500',
                      )}
                    />

                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-chip border border-line bg-raised font-display text-[0.6875rem] font-bold text-muted">
                      {initials(lead.name)}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-body', lead.isRead ? 'text-muted' : 'font-medium text-strong')}>
                        {lead.name}
                      </span>
                      <span className="flex items-center gap-1.5 truncate font-mono text-code text-faint">
                        <Phone aria-hidden className="h-3 w-3 shrink-0" strokeWidth={1.85} />
                        {lead.contact}
                      </span>
                    </span>

                    <span className="hidden min-w-0 flex-[1.4] truncate text-ui text-muted lg:block">
                      {lead.message ?? '—'}
                    </span>

                    {lead.utmSource && (
                      <Chip tone="quiet" className="hidden xl:inline-flex" mono>
                        {lead.utmSource}
                      </Chip>
                    )}

                    <span className="hidden sm:block">
                      <Chip tone={statusTones[lead.status]}>{statusLabels[lead.status]}</Chip>
                    </span>

                    <span className="w-24 shrink-0 whitespace-nowrap text-right text-ui text-faint">
                      {formatRelative(lead.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Drawer
        open={Boolean(selected)}
        onOpenChange={(isOpen) => !isOpen && setSelected(null)}
        title={selected?.name ?? ''}
        description={selected ? formatDate(selected.createdAt, true) : undefined}
      >
        {selected && (
          <div className="flex flex-col gap-5">
            <a
              href={`tel:${selected.contact}`}
              className="flex items-center gap-2 rounded-card border border-line px-4 py-3 font-mono text-body text-strong transition-ui duration-instant hover:border-line-strong"
            >
              <Phone aria-hidden className="h-4 w-4 text-brand-500" />
              {selected.contact}
            </a>

            {selected.message && (
              <div>
                <p className="eyebrow mb-1.5">{t.leads.message}</p>
                <p className="whitespace-pre-wrap text-body leading-relaxed text-strong">{selected.message}</p>
              </div>
            )}

            <div>
              <p className="eyebrow mb-1.5">{t.common.filter}</p>
              <Select
                value={selected.status}
                onValueChange={async (value) => {
                  await api.patch(`/leads/${selected.id}`, { status: value });
                  setSelected({ ...selected, status: value as LeadStatus });
                  void invalidate(['leads']);
                }}
                options={(Object.keys(statusLabels) as LeadStatus[]).map((key) => ({
                  value: key,
                  label: statusLabels[key],
                }))}
              />
            </div>

            {/* Attribution is the entire point of storing these extra columns
                (§7) — showing them is what makes the ad spend measurable. */}
            <dl className="flex flex-col gap-2 border-t border-line pt-4 text-ui">
              <Row label={t.leads.landingPage} value={selected.landingPath} mono />
              <Row label={t.leads.source} value={selected.utmSource ?? selected.source} />
              <Row label={t.leads.campaign} value={selected.utmCampaign} />
              <Row label="Referrer" value={selected.referrer} mono />
              <Row label="Til" value={selected.locale?.toUpperCase()} />
            </dl>
          </div>
        )}
      </Drawer>
    </>
  );
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className={cn('min-w-0 truncate text-right text-strong', mono && 'font-mono text-code')}>{value || '—'}</dd>
    </div>
  );
}
